import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, MapPin, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// AJ's actual images
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_a68a521f-d588-4c7a-99ce-4eb4c9c2d03b/artifacts/blvr559f_IMG_3348.jpeg";
const COACH_IMAGE = "https://customer-assets.emergentagent.com/job_fit-life-coach/artifacts/z5x3jaiu_6957FE8B-9790-4174-A272-97B5877E41F2.png";

const Services = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [services, setServices] = useState([]);
  const [trainingLocations, setTrainingLocations] = useState([]);

  useEffect(() => {
    setIsVisible(true);
  }, [setIsVisible]);

  useEffect(() => {
    fetch(`${API_URL}/api/content/services`)
      .then(res => res.json())
      .then(setServices)
      .catch(() => {});
    fetch(`${API_URL}/api/content/locations`)
      .then(res => res.json())
      .then(data => setTrainingLocations(data.trainingLocations || []))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-end">
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_IMAGE}
            alt="AJ Javier - Personal Training"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        </div>
        <div className={`container relative z-10 pb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h1 className="heading-display text-white">Coaching</h1>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-[#F5F0EB] py-20 md:py-28">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-large mb-6">Personal Training Services</h2>
            <p className="body-large">
              Whether you prefer 1-to-1 sessions, partner training, group sessions, or strength-focused 
              programming, I'll create a plan that fits your goals, schedule, and lifestyle.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={service.id} className="border border-[#E5E0DB] p-8">
                <h3 className="heading-medium mb-4">{service.title}</h3>
                <p className="body-regular text-[#6B6B6B] mb-6">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex gap-3">
                      <CheckCircle size={16} className="text-[#C9A962] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-[#6B6B6B]">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="bg-[#EDE8E3] py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="heading-large mb-8">What to Expect</h2>
              <div className="space-y-8">
                {[
                  { num: '01', title: 'Free Consultation', desc: "We'll discuss your goals, experience, and any limitations to ensure we're a good fit." },
                  { num: '02', title: 'Custom Programme', desc: "Based on our chat, I'll create a training plan tailored specifically to you." },
                  { num: '03', title: 'Regular Sessions', desc: 'We train together at a frequency that works for your schedule and goals.' },
                  { num: '04', title: 'Ongoing Support', desc: 'Programme adjustments, guidance, and accountability between sessions.' },
                ].map((item) => (
                  <div key={item.num} className="flex gap-6">
                    <span className="text-3xl font-semibold text-[#C9A962]" style={{fontFamily: "'Oswald', sans-serif"}}>{item.num}</span>
                    <div>
                      <h4 className="heading-small mb-2">{item.title}</h4>
                      <p className="body-regular text-[#6B6B6B]">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src={COACH_IMAGE}
                alt="AJ Javier - Head Coach"
                className="w-full aspect-[4/5] object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Locations with Links */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <h2 className="heading-large text-center mb-6">Training Locations</h2>
          <p className="body-regular text-center text-[#6B6B6B] mb-16 max-w-2xl mx-auto">
            I offer sessions at multiple locations across East London. Click on any gym location to view on Google Maps.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {trainingLocations.map((location) => (
              <div key={location.name} className="border border-[#E5E0DB] p-6 hover:border-[#C9A962] transition-colors duration-300">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="heading-small">{location.name}</h3>
                  <span className={`text-[10px] font-semibold tracking-[0.1em] uppercase px-2 py-1 ${
                    location.tag === 'Base' 
                      ? 'bg-[#C9A962] text-[#1A1A1A]' 
                      : location.tag === 'Premium'
                      ? 'bg-[#1A1A1A] text-white'
                      : 'bg-[#EDE8E3] text-[#1A1A1A]'
                  }`}>
                    {location.tag}
                  </span>
                </div>
                <p className="body-small mb-4">{location.desc}</p>
                {location.mapUrl ? (
                  <a 
                    href={location.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium text-[#C9A962] hover:text-[#1A1A1A] transition-colors duration-300"
                  >
                    <MapPin size={14} />
                    View on Google Maps
                    <ExternalLink size={12} />
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-2 text-sm text-[#6B6B6B]">
                    <MapPin size={14} />
                    Various locations
                  </span>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/pricing" className="btn-outline">
              View Full Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1A1A1A] py-20 md:py-28">
        <div className="container text-center">
          <h2 className="heading-large text-white mb-6">Ready to Get Started?</h2>
          <p className="body-large text-gray-400 mb-10 max-w-xl mx-auto">
            Book a free consultation to discuss your goals and find the right training option.
          </p>
          <Link to="/contact" className="btn-gold inline-flex items-center gap-2">
            Book Free Consultation
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
